import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/contexts/auth'
import { LanguageProvider } from '@/contexts/language'
import { AppModeProvider } from '@/contexts/appMode'
import { RequireAuth, RequireAdmin, RequireAgent, RequireMaster } from '@/components/auth/guards'
import { AdminShell } from '@/components/layout/AdminShell'

import LoginPage from '@/pages/login'

// Admin pages
import AdminDashboardPage   from '@/pages/admin/dashboard'
import AdminRoundsPage      from '@/pages/admin/rounds'
import AdminBetsPage        from '@/pages/admin/bets'
import AdminGamesPage       from '@/pages/admin/games'
import AdminDrawsPage       from '@/pages/admin/draws'
import AdminSettlementsPage from '@/pages/admin/settlements'
import AdminPartnerWalletPage from '@/pages/admin/partner-wallet'
import AdminKenoLimitsPage from '@/pages/admin/keno-limits'
import AdminReportsPage     from '@/pages/admin/reports'
import AdminProvidersPage   from '@/pages/admin/providers'
import AdminAuditPage       from '@/pages/admin/audit-logs'
import AdminIpWhitelistPage from '@/pages/admin/ip-whitelist'
import AdminProfilePage     from '@/pages/admin/profile'
import AdminNumberLimitsPage from '@/pages/admin/number-limits'
import AdminNumberLimitHistoryPage from '@/pages/admin/number-limit-history'
import AdminPayoutRatesPage  from '@/pages/admin/payout-rates'
import AdminMastersPage      from '@/pages/admin/masters'
import AdminMasterAgentsPage from '@/pages/admin/master-agents'
import AdminAgentMembersPage from '@/pages/admin/agent-members'
import AdminsPage           from '@/pages/admin/admins'
import AdminSettingsPage    from '@/pages/admin/settings'
import AdminBannersPage     from '@/pages/admin/banners'
import AgentsDocsPage               from '@/pages/admin/agents-docs'
import AdminReportsMasterAgentsPage from '@/pages/admin/reports-master-agents'
import AdminReportsAgentPage        from '@/pages/admin/reports-agent'
import AdminReportsAgentMemberPage  from '@/pages/admin/reports-agent-member'
import StagingTestPage          from '@/pages/admin/staging/test'
import StagingRunsPage          from '@/pages/admin/staging/runs'
import StagingRunDetailPage     from '@/pages/admin/staging/run-detail'
import StagingReportsPage       from '@/pages/admin/staging/reports'

// Agent pages
import AgentDashboardPage   from '@/pages/agent/dashboard'
import AgentMembersPage     from '@/pages/agent/members'
import AgentReportsPage     from '@/pages/agent/reports'
import AgentReportsMemberPage from '@/pages/agent/reports-member'
import AgentProfilePage     from '@/pages/agent/profile'
import AgentSettingsPage    from '@/pages/agent/settings'
import AgentNumberRulesPage from '@/pages/agent/number-rules'
import AgentNumberHistoryPage from '@/pages/agent/number-history'
import AgentKeysPage        from '@/pages/agent/keys'

// Master pages
import MasterDashboardPage  from '@/pages/master/dashboard'
import MasterAgentsPage     from '@/pages/master/agents'
import MasterAgentDetailPage from '@/pages/master/agent-detail'
import MasterReportsPage    from '@/pages/master/reports'

export default function App() {
  return (
    <BrowserRouter>
      <AppModeProvider>
        <AuthProvider>
          <LanguageProvider>
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<LoginPage />} />

              {/* Authenticated routes */}
              <Route element={<RequireAuth />}>

                {/* Admin-only routes */}
                <Route element={<RequireAdmin />}>
                  <Route path="/admin" element={<AdminShell />}>
                    <Route index element={<AdminDashboardPage />} />
                    <Route path="rounds"           element={<AdminRoundsPage />} />
                    <Route path="bets"             element={<AdminBetsPage />} />
                    <Route path="games"            element={<AdminGamesPage />} />
                    <Route path="draws"            element={<AdminDrawsPage />} />
                    <Route path="number-limits"    element={<AdminNumberLimitsPage />} />
                    <Route path="number-limit-history" element={<AdminNumberLimitHistoryPage />} />
                    <Route path="payout-rates"     element={<AdminPayoutRatesPage />} />
                    <Route path="settlements"      element={<AdminSettlementsPage />} />
                    <Route path="partner-wallet"   element={<AdminPartnerWalletPage />} />
                    <Route path="keno-limits"      element={<AdminKenoLimitsPage />} />
                    <Route path="reports"          element={<AdminReportsPage />} />
                    <Route path="reports/master/:masterId" element={<AdminReportsMasterAgentsPage />} />
                    <Route path="reports/agent/:partnerId" element={<AdminReportsAgentPage />} />
                    <Route path="reports/agent/:partnerId/member/:memberId" element={<AdminReportsAgentMemberPage />} />
                    <Route path="providers"        element={<AdminProvidersPage />} />
                    <Route path="audit-logs"       element={<AdminAuditPage />} />
                    <Route path="ip-whitelist"     element={<AdminIpWhitelistPage />} />
                    <Route path="profile"          element={<AdminProfilePage />} />
                    <Route path="masters"          element={<AdminMastersPage />} />
                    <Route path="masters/:masterId/agents" element={<AdminMasterAgentsPage />} />
                    <Route path="masters/:masterId/agents/:agentId" element={<AdminAgentMembersPage />} />
                    <Route path="admins"           element={<AdminsPage />} />
                    <Route path="settings"         element={<AdminSettingsPage />} />
                    <Route path="banners"          element={<AdminBannersPage />} />
                    {/* Integration tester — its own menu group, separate from reports. */}
                    <Route path="staging/test"          element={<StagingTestPage />} />
                    <Route path="staging/runs"          element={<StagingRunsPage />} />
                    <Route path="staging/runs/:runId"   element={<StagingRunDetailPage />} />
                    <Route path="staging/reports"       element={<StagingReportsPage />} />
                  </Route>
                </Route>

                {/* Agent-only routes */}
                <Route element={<RequireAgent />}>
                  <Route path="/agent" element={<AdminShell />}>
                    <Route index element={<AgentDashboardPage />} />
                    <Route path="members"                   element={<AgentMembersPage />} />
                    <Route path="reports"                   element={<AgentReportsPage />} />
                    <Route path="reports/member/:memberId"  element={<AgentReportsMemberPage />} />
                    <Route path="profile"                   element={<AgentProfilePage />} />
                    <Route path="number-rules"              element={<AgentNumberRulesPage />} />
                    <Route path="number-history"            element={<AgentNumberHistoryPage />} />
                    <Route path="settings"                  element={<AgentSettingsPage />} />
                    <Route path="keys"                      element={<AgentKeysPage />} />
                    <Route path="docs"                      element={<AgentsDocsPage />} />
                  </Route>
                </Route>

                {/* Master-only routes (partner with level='master') */}
                <Route element={<RequireMaster />}>
                  <Route path="/master" element={<AdminShell />}>
                    <Route index element={<MasterDashboardPage />} />
                    <Route path="agents"          element={<MasterAgentsPage />} />
                    <Route path="agents/:agentId" element={<MasterAgentDetailPage />} />
                    <Route path="reports"         element={<MasterReportsPage />} />
                  </Route>
                </Route>

              </Route>

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </LanguageProvider>
        </AuthProvider>
      </AppModeProvider>
    </BrowserRouter>
  )
}
